import React from 'react';
import { TimeDisplay } from './TimeDisplay';
import { ViewModeSelector } from './ViewModeSelector';
import { Users, Pencil } from 'lucide-react';
import { EditableRoomDisplayName } from '../EditableRoomDisplayName';
import { resolvedRoomDisplayName } from '../../roomDisplayName';
import { StreamAudioControl } from './StreamAudioControl';
import { LicenseInfo } from '../../lib/LicenseInfo';

interface FooterBarProps {
    roomId: string;
    roomDisplayName?: string;
    onRoomDisplayNameSave?: (name: string) => void;
    participantCount: number;
    onMembersClick: () => void;
    viewMode: 'democratic' | 'sidebar' | 'custom';
    onViewModeChange: (mode: 'democratic' | 'sidebar' | 'custom') => void;
    /** Receiver: show master audio controls when there are remote streams */
    receiverAudioUi?: boolean;
    receiverVolume?: number;
    /** Mute icon + UX: includes volume 0, user mute, or autoplay-blocked */
    receiverDisplayMuted?: boolean;
    receiverAudioNeedsInteraction?: boolean;
    onReceiverVolumeChange?: (volume: number) => void;
    onReceiverMutedToggle?: () => void;
    receiverHasAudioTrack?: boolean;
    receiverAnnotationUi?: boolean;
    receiverAnnotationEnabled?: boolean;
    onReceiverAnnotationToggle?: () => void;
}

export const FooterBar: React.FC<FooterBarProps> = ({ 
    roomId,
    roomDisplayName,
    onRoomDisplayNameSave,
    participantCount, 
    onMembersClick, 
    viewMode, 
    onViewModeChange,
    receiverAudioUi = false,
    receiverVolume = 1,
    receiverDisplayMuted = false,
    receiverAudioNeedsInteraction = false,
    onReceiverVolumeChange,
    onReceiverMutedToggle,
    receiverHasAudioTrack = false,
    receiverAnnotationUi = false,
    receiverAnnotationEnabled = false,
    onReceiverAnnotationToggle,
}) => {
    const label = resolvedRoomDisplayName(roomDisplayName);
    const editable = Boolean(onRoomDisplayNameSave);
    return (
        <div className="fixed bottom-0 left-0 right-0 z-40 backdrop-blur-sm" style={{ backgroundColor: 'rgb(5 7 36 / 90%)' }}>
            <div className="flex items-center justify-between px-6 py-3">
                {/* Left Side: Room Info & Details */}
                <div className="flex items-center gap-4 text-white/80 text-sm">
                    <TimeDisplay />
                    <span className="text-white/30">|</span>
                    <EditableRoomDisplayName
                        value={label}
                        onSave={(name) => onRoomDisplayNameSave?.(name)}
                        editable={editable}
                        className="text-white/80"
                        inputClassName="bg-white/10 border border-white/20 rounded px-2 py-0.5 text-white text-sm max-w-[200px]"
                    />
                    <span className="text-white/30">|</span>
                    <span>ellington.inshare.ai</span>
                    <span className="text-white/30">|</span>
                    <span className="font-semibold tracking-wider">{roomId}</span>
                    {/* License expiry — renders nothing on unlicensed builds */}
                    <LicenseInfo className="hidden md:inline text-white/45 before:content-['|'] before:mr-4 before:text-white/30" />
                    {receiverAudioUi && onReceiverVolumeChange && onReceiverMutedToggle && (
                        <>
                            <span className="text-white/30">|</span>
                            <StreamAudioControl
                                volume={receiverVolume}
                                displayMuted={receiverDisplayMuted}
                                onVolumeChange={onReceiverVolumeChange}
                                onMutedToggle={onReceiverMutedToggle}
                                hasAudioTrack={receiverHasAudioTrack}
                                needsUserGesture={receiverAudioNeedsInteraction}
                            />
                        </>
                    )}
                    {receiverAnnotationUi && onReceiverAnnotationToggle && (
                        <>
                            <span className="text-white/30">|</span>
                            <button
                                onClick={onReceiverAnnotationToggle}
                                aria-label={receiverAnnotationEnabled ? 'Disable annotation mode' : 'Enable annotation mode'}
                                className={`inline-flex h-8 w-8 items-center justify-center rounded-md text-sm transition-colors ${
                                    receiverAnnotationEnabled
                                        ? 'bg-cyan-500/25 text-cyan-200 hover:bg-cyan-500/35'
                                        : 'bg-white/10 text-white hover:bg-white/20'
                                }`}
                                title={receiverAnnotationEnabled ? 'Annotation: On' : 'Annotate'}
                            >
                                <Pencil className="h-4 w-4" />
                            </button>
                        </>
                    )}
                </div>

                {/* Right Side: Members, View Buttons & Logo */}
                <div className="flex items-center gap-3">
                    {/* Members Button */}
                    <button
                        onClick={onMembersClick}
                        className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors text-white text-sm"
                    >
                        <Users className="w-4 h-4" />
                        <span>Members ({participantCount})</span>
                    </button>

                    {/* View Mode Selector */}
                    <ViewModeSelector mode={viewMode} onChange={onViewModeChange} />
                    
                    {/* Logo */}
                    <img src="./EllingtonLogo.svg" className="h-8 ml-2" alt="logo" />
                </div>
            </div>
        </div>
    );
};
